import { DebentureApiResponse, CadastroDebentureApiRequest, InstrumentoFinanceiro, TipoRegistroInstrumentoFinanceiro, ReservarUltimoNumeroDaEmissaoRequest, GrupoAcesso } from 'common/genericTypes';
import Formulario from 'pages/debenture/registro/formulario';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, RouteComponentProps } from 'react-router-dom';
import { RootState } from 'store';
import { highlightErrors, typeOfRegister } from 'store/modules/debenture';
import {
  resetDebentureDetails,
  updateDebentureCaracteristicas,
  updateField,
} from 'store/modules/debenture/caracteristicas';
import { resetCriteriosRemuneracao, updateCriteriosRemuneracao } from 'store/modules/debenture/criteriosRemuneracao';
import { resetEventos, updateEventos } from 'store/modules/debenture/eventos';
import { resetDocuments, updateDocuments } from 'store/modules/debenture/uploadDocumentos';
import { updateId, resetEmissor } from 'store/modules/debenture/emissor';
import { UploadDocResponse } from 'store/modules/models/uploadDocResponse';
import DebentureParser from 'utils/parsers/debentureParser';
import { addAlert } from 'store/modules/snackBar';
import { reprocessarValidacao } from 'requests/divergencias';

type ComponentParams = { debentureId?: string };
type ComponentState = { 
  debentureRascunho?: DebentureApiResponse; 
  acao?: TipoRegistroInstrumentoFinanceiro;
  instrumentoData?: {
    id: number;
    codigo: string;
    tipoInstrumentoFinanceiro: string;
    origem?: string;
    statusValidacao?: string;
    dataDeCriacao: Date;
  };
};

type Props = RouteComponentProps<ComponentParams, {}, ComponentState>;

const Debenture: React.FC<Props> = (props) => {
  const { debentureId } = props.match.params;
  const debentureRascunho = props.location.state?.debentureRascunho;
  const instrumentoData = props.location.state?.instrumentoData;

  const history = useHistory();
  const dispatch = useDispatch();
  const participanteId = useSelector((state: RootState) => state.auth.usuario.participante.id);
  const usuarioGrupoAcesso = useSelector((state: RootState) => state.auth.usuario.grupoAcesso);

  const caracteristicas = useSelector((state: RootState) => state.debentureCadastro.caracteristicas);
  const criteriosRemuneracao = useSelector((state: RootState) => state.debentureCadastro.criteriosRemuneracao);
  const eventos = useSelector((state: RootState) => state.debentureCadastro.eventos);
  const uploadDocumentos = useSelector((state: RootState) => state.debentureCadastro.uploadDocumentos);
  const emissor = useSelector((state: RootState) => state.debentureCadastro.emissor);
  const debentureApi = useSelector((state: RootState) => state.debentureApi);
  const instrumentoApi = useSelector((state: RootState) => state.instrumentoApi);

  const [codigo, setCodigo] = useState<string>('');
  const [reprocessamentoExecutado, setReprocessamentoExecutado] = useState<boolean>(false);
  const [typeOfFormulario, setTypeOfFormulario] = useState<TipoRegistroInstrumentoFinanceiro>(TipoRegistroInstrumentoFinanceiro.Cadastro);

  const submitPutDebenture = async (
    debentureId: string,
    debenture: CadastroDebentureApiRequest
  ) => {
    let result = { codigo: '', ticket: '' };
    try {
      const res = await debentureApi.putDebenture(dispatch, debentureId, debenture, true);
      result = { codigo: res.codigo, ticket: res.ticket };
    } catch (error) { }
    return result;
  };

  const submitPostDebenture = async (): Promise<DebentureApiResponse> => {
    try {
      const debenture: CadastroDebentureApiRequest = DebentureParser.stateToCadastroDebentureApiRequest(
        caracteristicas,
        criteriosRemuneracao,
        eventos,
        uploadDocumentos,
        emissor
      );
      return await debentureApi.postDebenture(dispatch, debenture, true);
    } catch (error) {
      return {} as DebentureApiResponse;
    }
  };

  const submitDebentureDocument = async (
    debentureId: string,
    emissionType: string,
    file: File
  ): Promise<UploadDocResponse[]> => {
    try {
      return debentureApi.putUploadDoc(dispatch, debentureId, emissionType, file, true);
    } catch (error) {
      return [];
    }
  };

  const handleEditSubmit = async () => {
    if (!debentureId) return { codigo: '', ticket: '' };

    const debenture: CadastroDebentureApiRequest = DebentureParser.stateToCadastroDebentureApiRequest(
      caracteristicas,
      criteriosRemuneracao,
      eventos,
      uploadDocumentos,
      emissor
    );

    const debentureResponse = await submitPutDebenture(debentureId, debenture);

    for (const document of uploadDocumentos.documents) {
      if (document.file) {
        const uploadResponse = await submitDebentureDocument(debentureId, document.type, document.file);
        if (!uploadResponse || uploadResponse.length === 0) return { codigo: '', ticket: '' };
      }
    }

    if (caracteristicas.id) {
      for (const documentToDelete of uploadDocumentos.documentsToDelete) {
        documentToDelete.remoteFileInfo?.id &&
          (await debentureApi.deleteUploadDoc(dispatch, debentureId, documentToDelete.remoteFileInfo.id, true));
      }
    }

    setReprocessamentoExecutado(true);
    if (instrumentoData && 
        instrumentoData.origem === 'FRONTIER' && 
        instrumentoData.statusValidacao === 'DIVERGENTE' &&
        usuarioGrupoAcesso === GrupoAcesso.Operacoes) {
      try {
        await reprocessarValidacao(dispatch, { 
          instrumentoFinanceiroId: instrumentoData.id.toString() 
        }, true);
      } catch (error) {
        console.error('Erro ao reprocessar validação:', error);
      }
    }

    return debentureResponse;
  };

  const handleRegisterSubmit = async () => {
    const uploadedDebenture = await submitPostDebenture();

    if (uploadedDebenture.id) {
      for (const document of uploadDocumentos.documents) {
        if (document.file) {
          await submitDebentureDocument(uploadedDebenture.debentureId, document.type, document.file);
        }
      }
    }

    return { codigo: '', ticket: uploadedDebenture.ticket };
  };

  const handleAditamentoSubmit = async (): Promise<{ codigo: string, ticket: string } | undefined> => {
    try {
      if (codigo) {
        setReprocessamentoExecutado(true);
        if (instrumentoData && 
            instrumentoData.origem === 'FRONTIER' && 
            instrumentoData.statusValidacao === 'DIVERGENTE' &&
            usuarioGrupoAcesso === GrupoAcesso.Operacoes) {
          try {
            await reprocessarValidacao(dispatch, { 
              instrumentoFinanceiroId: instrumentoData.id.toString() 
            }, true);
          } catch (error) {
            console.error('Erro ao reprocessar validação:', error);
          }
        }
        
        return { codigo, ticket: '' };
      } else throw 'ID do Debênture não identificado';
    } catch (error) {
      console.error(error);
    }
  };

  const handleReturnToList = () => {
    history.push('/solicitacoes');
  };

  const handleSalvarRascunho = async () => {
    const numeroReservado = await reservarNumeroEmissao();
    if (!numeroReservado) return '';  

    let draftId = '';
    try {
      const rascunho = DebentureParser.stateToCadastroDebentureApiRequest(
        caracteristicas,
        criteriosRemuneracao,
        eventos,
        uploadDocumentos,
        emissor
      );

      const documents = uploadDocumentos.documents
        .filter((doc) => !!doc.file)
        .map((doc) => ({ file: doc.file!, emissionType: doc.type }));

      const result = caracteristicas.draftId
        ? await instrumentoApi.putRascunho(
          dispatch,
          InstrumentoFinanceiro.Debenture,
          caracteristicas.draftId,
          rascunho,
          documents,
          true
        )
        : await instrumentoApi.postRascunho(dispatch, InstrumentoFinanceiro.Debenture, rascunho, documents, true);
      setCodigo(result.codigo);
      draftId = result.draftId;
      dispatch(updateField({ name: 'draftId', value: draftId }));

      dispatch(addAlert({ message: `Rascunho salvo com sucesso. Código do Instrumento Financeiro ${result.codigo}`, severity: 'success' }));
    } catch (error) { }
    return draftId;
  };

  const defineTypeOfRegister = async (): Promise<void> => {
    if (debentureId) {
      if (props.location.state?.acao) {
        setTypeOfFormulario(props.location.state.acao);
        dispatch(typeOfRegister(props.location.state.acao));
      }
    } else {
      setTypeOfFormulario(TipoRegistroInstrumentoFinanceiro.Cadastro);
      dispatch(typeOfRegister(TipoRegistroInstrumentoFinanceiro.Cadastro));
    }
  };

  const reserveBeforeHandleSubmit = async (handleSubmit: () => Promise<{ codigo: string; ticket: string } | undefined>): Promise<{ codigo: string; ticket: string } | undefined> => {
    const numeroReservado = await reservarNumeroEmissao();
    if (!numeroReservado) return;

    return await handleSubmit();
  }

  const reservarNumeroEmissao = async () => {
    if (!caracteristicas.numeroEmissao) {
      dispatch(addAlert({ message: 'Número de emissão não encontrado', severity: 'error' }));
      return false;
    }
    if (!caracteristicas?.cnpjEmissor) {
      dispatch(addAlert({ message: 'CNPJ do emissor não encontrado', severity: 'error' }));
      return false;
    }

    try {
      const reservaRequest: ReservarUltimoNumeroDaEmissaoRequest = {
        emissorCnpj: caracteristicas.cnpjEmissor,
        numeroProximaEmissao: caracteristicas.numeroEmissao,
        tipoInstrumento: InstrumentoFinanceiro.Debenture,
        externalId: participanteId,
      };

      await instrumentoApi.postReservarUltimoNumeroDaEmissao(dispatch, reservaRequest, true);
      return true;
    } catch (error) {
      dispatch(addAlert({ message: 'Erro ao reservar número de emissão', severity: 'error' }));
      return false;
    }
  };

  useEffect(() => {
    dispatch(resetEmissor());
    dispatch(resetDebentureDetails());
    dispatch(resetCriteriosRemuneracao());
    dispatch(resetEventos());
    dispatch(resetDocuments());
    dispatch(highlightErrors(false));
  }, []);

  useEffect(() => {
    if (debentureId) {
      debentureApi
        .getDebentureDetalhes(dispatch, debentureId, true)
        .then((response) => {
          if (response) {
            setCodigo(response.codigo);
            dispatch(updateId(response.emissorId ?? ''));
            dispatch(updateDebentureCaracteristicas(DebentureParser.apiResponseToCaracteristicasState(response)));
            dispatch(updateCriteriosRemuneracao(DebentureParser.apiResponseToCriteriosRemuneracaoState(response)));
            dispatch(updateEventos(DebentureParser.apiResponseToEventosState(response)));
            dispatch(updateDocuments(DebentureParser.apiResponseToDebentureDocumentsState(response)));
          }
        })
        .catch(() => { });
    } else if (debentureRascunho) {
      dispatch(updateId(debentureRascunho.emissorId ?? ''));
      dispatch(
        updateDebentureCaracteristicas(DebentureParser.apiResponseToCaracteristicasState(debentureRascunho, false))
      );
      dispatch(updateCriteriosRemuneracao(DebentureParser.apiResponseToCriteriosRemuneracaoState(debentureRascunho)));
      dispatch(updateEventos(DebentureParser.apiResponseToEventosState(debentureRascunho)));
      dispatch(updateDocuments(DebentureParser.apiResponseToDebentureDocumentsState(debentureRascunho)));
    }
    defineTypeOfRegister();
  }, [debentureApi, debentureId, debentureRascunho, dispatch]);

  const component = () => {
    switch (typeOfFormulario) {
      case TipoRegistroInstrumentoFinanceiro.Cadastro:
        return (
          <div>
            <Formulario
              debentureId={debentureId}
              title={`Cadastro Debênture`}
              handleSubmit={() => reserveBeforeHandleSubmit(handleRegisterSubmit)}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit={false}
            />
          </div>
        );
      case TipoRegistroInstrumentoFinanceiro.Edicao:
        return (
          <div>
            <Formulario
              debentureId={debentureId}
              title={'Editar Debênture'}
              handleSubmit={handleEditSubmit}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit={false}
            />
          </div>
        );
      case TipoRegistroInstrumentoFinanceiro.EdicaoEmitido:
        return (
          <div>
            <Formulario
              title={`Editar ${codigo}`}
              debentureId={debentureId}
              handleSubmit={handleAditamentoSubmit}
              handleReturn={() => history.push('/instrumentos/gerenciar')}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit
              codigo={codigo}
              instrumentoData={instrumentoData}
              reprocessamentoExecutado={reprocessamentoExecutado}
            />
          </div>
        );
      case TipoRegistroInstrumentoFinanceiro.Aditamento:
        return (
          <div>
            <Formulario
              title={'Aditar Debênture'}
              debentureId={debentureId}
              handleSubmit={handleAditamentoSubmit}
              handleReturn={handleReturnToList}
              handleSalvarRascunho={handleSalvarRascunho}
              isEdit
              codigo={codigo}
            />
          </div>
        );
      default:
        return <p>Loading</p>
    }
  };
  return component();
};

export default Debenture;
